import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import {
  AUDIT_INTEGRITY_STATEMENTS,
  AUDIT_TAB_LABELS,
  auditExportTable,
  CHANGE_EXPORT_COLUMNS,
  formatAuditDate,
  formatAuditTime,
  type AuditExportSelection,
} from './auditModel'

/**
 * PD-1322 (S11) — PDF export of an audit tab, built with pdfmake following
 * `utils/progressNotePdf.ts` (the paginated multi-section table document in this repo).
 * No new dependency: pdfmake is already used by two other exports.
 *
 * WHICH rows this carries is not decided here — `selectAuditExportRows` picks them and
 * `auditExportTable` projects the cells, both shared with the CSV export (audit-csv.ts),
 * so the two artefacts cannot disagree about what "the filtered set" was.
 *
 * Unlike the CSV, this file leaves the building as a standalone document: nobody reading it
 * months later has the filter bar, the truncation banner, or the operator's session for
 * context. So the document states its own scope (patient, tab, filters, who, when) and says
 * so loudly when the row set it carries is incomplete.
 */

const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

const DIM = '#7D7987'
const WARN_BG = '#FEF3C7'
const WARN_TEXT = '#78350F'

export type AuditPdfHeader = {
  /** Patient as the reader identifies them — name plus id, never the id alone. */
  patientLabel: string
  /** The authenticated operator who ran the export. */
  generatedBy: string
  /** ISO instant of the click. */
  generatedAt: string
  /** Every filter dimension and its value — see `describeAuditFilters`. */
  filters: Array<[string, string]>
}

/**
 * Per-tab column widths, ONE PER COLUMN — pdfmake matches them positionally, so a short
 * array silently shifts every cell after the gap into the wrong heading. The change tab
 * carries the most columns, so it gets the tight set; Before/After take `*` so they share
 * whatever the fixed columns leave. `auditExportTable` owns the column lists
 * (ACCESS_EXPORT_COLUMNS / CHANGE_EXPORT_COLUMNS); audit-pdf.test.ts holds these to the
 * same length so adding a column cannot land here unnoticed.
 */
export const COLUMN_WIDTHS: Record<AuditExportSelection['tab'], (string | number)[]> = {
  access: [140, 110, '*', 60, 90],
  changes: [130, 100, 95, 60, 70, '*', '*'],
}

/**
 * QA round 1 (W1) — the change tab's Before/After cells carry raw record values
 * (JSON-ish compact summaries with no spaces). pdfmake only breaks lines at word
 * boundaries — `splitWords` finds none in an unbroken value, so the whole value stayed
 * on one line and ran past the column (and, for After, the rightmost column, off the
 * page): the clipping QA saw. `wordBreak: 'break-all'` (a real pdfmake style property,
 * textTools.js `getStyleProperty(... 'wordBreak' ...) === 'break-all'`) lets those two
 * cells break anywhere, so long values wrap inside their `*` columns. The column
 * indexes track CHANGE_EXPORT_COLUMNS (auditModel.ts).
 */
const BREAK_ANYWHERE_COLUMNS: Record<AuditExportSelection['tab'], ReadonlySet<number>> = {
  access: new Set<number>(),
  changes: new Set([
    CHANGE_EXPORT_COLUMNS.indexOf('Before'),
    CHANGE_EXPORT_COLUMNS.indexOf('After'),
  ]),
}

/**
 * The reader's own zone, named rather than assumed. Row timestamps are rendered in it (the
 * same `formatAuditDate`/`formatAuditTime` the tables use), so a detached document has to
 * say which zone that was or every time in it is ambiguous.
 */
function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'
  } catch {
    return 'local time'
  }
}

function labelledValue(label: string, value: string): Content {
  return { text: [{ text: `${label}: `, bold: true }, value], margin: [0, 0, 0, 2] }
}

export function buildAuditPdfDefinition(
  selection: AuditExportSelection,
  header: AuditPdfHeader,
): TDocumentDefinitions {
  const { columns, rows } = auditExportTable(selection)
  const tabLabel = AUDIT_TAB_LABELS[selection.tab]
  const zone = localZoneName()
  const content: Content[] = []

  content.push(
    { text: `Chart Audit — ${tabLabel}`, style: 'header' },
    {
      // Per tab: only the access table is append-only in the database. Saying it of a
      // change export would be a false assurance in a detached document, where nobody can
      // check it. See AUDIT_INTEGRITY_STATEMENTS.
      text: `Compliance export. ${AUDIT_INTEGRITY_STATEMENTS[selection.tab]}.`,
      color: DIM,
      fontSize: 9,
      margin: [0, 2, 0, 10],
    },
  )

  // --- Scope: what this file is, so it stays attributable away from the UI ---
  content.push(
    { text: 'Export scope', style: 'subheader', margin: [0, 0, 0, 6] },
    {
      columns: [
        {
          width: '50%',
          stack: [
            labelledValue('Patient', header.patientLabel),
            labelledValue('Tab', tabLabel),
            labelledValue('Events in this export', String(rows.length)),
          ],
        },
        {
          width: '50%',
          stack: [
            labelledValue('Generated by', header.generatedBy),
            labelledValue('Generated (UTC)', new Date(header.generatedAt).toISOString()),
            labelledValue(
              'Generated (local)',
              `${formatAuditDate(header.generatedAt)} ${formatAuditTime(header.generatedAt)} (${zone})`,
            ),
          ],
        },
      ],
      margin: [0, 0, 0, 8],
    },
    {
      text: `All times in the table below are ${zone}.`,
      color: DIM,
      fontSize: 8,
      margin: [0, 0, 0, 8],
    },
  )

  // --- Incompleteness, stated in the document rather than only in the banner ---
  //
  // TWO statements, in one block, sync before cap. They are unrelated causes calling for
  // different actions: a derivation still running will fill the gap on its own, a capped
  // page never will. Collapsing them into one sentence — which the old single `truncated`
  // flag forced — told a reader to narrow their filters when the honest answer was "wait",
  // and told them to wait when the answer was "this window is full".
  const incompleteness: string[] = []
  if (selection.syncPending) {
    incompleteness.push(
      "This chart's change history was still being derived when this export ran. Changes " +
        'older than the rows below may not have been read from the record yet; they are ' +
        'missing from this document rather than absent from the chart.',
    )
  }
  if (selection.capped) {
    incompleteness.push(
      `This export carries only the ${rows.length.toLocaleString()} most recent events matching ` +
        'the filters below. Older events match them and are NOT included, because the ' +
        'server caps how many events one query returns. Narrow the filters, or page further ' +
        'back in the tab, and export again to obtain the rest.',
    )
  }
  if (incompleteness.length > 0) {
    content.push({
      table: {
        widths: ['*'],
        body: [
          [
            {
              stack: [
                { text: 'INCOMPLETE RECORD', bold: true, margin: [0, 0, 0, 2] },
                ...incompleteness.map((text, index) => ({
                  text,
                  margin: [0, index === 0 ? 0 : 4, 0, 0] as [number, number, number, number],
                })),
              ],
              color: WARN_TEXT,
              fillColor: WARN_BG,
              margin: [6, 6, 6, 6],
            },
          ],
        ],
      },
      layout: 'noBorders',
      margin: [0, 0, 0, 10],
    })
  }

  // --- The filters in force, every dimension, narrowed or not ---
  content.push(
    { text: 'Filters applied', style: 'subheader', margin: [0, 0, 0, 6] },
    {
      table: {
        widths: [160, '*'],
        body: header.filters.map(([label, value]) => [{ text: label, bold: true }, value]),
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 12],
    },
  )

  // --- The rows ---
  content.push({ text: tabLabel, style: 'subheader', margin: [0, 0, 0, 6] })
  if (rows.length === 0) {
    content.push({
      text: 'No events matched these filters.',
      color: DIM,
      italics: true,
      fontSize: 9,
    })
  } else {
    content.push({
      table: {
        // pdfmake repeats the header row on every page it breaks onto.
        headerRows: 1,
        widths: COLUMN_WIDTHS[selection.tab],
        body: [
          columns.map((column) => ({ text: column, bold: true, fillColor: '#f0f0f0' })),
          ...rows.map((row) =>
            row.map((cell, columnIndex) =>
              // `wordBreak` is a runtime pdfmake style property @types/pdfmake does not
              // declare — see BREAK_ANYWHERE_COLUMNS for why these cells need it.
              BREAK_ANYWHERE_COLUMNS[selection.tab].has(columnIndex)
                ? ({ text: cell, wordBreak: 'break-all' } as unknown as Content)
                : { text: cell },
            ),
          ),
        ],
      },
      layout: 'lightHorizontalLines',
      fontSize: 8,
    })
  }

  return {
    pageSize: 'A4',
    // Landscape: the change tab is seven columns wide and its before/after cells carry
    // real values, which portrait turns into an unreadable stack of wrapped fragments.
    pageOrientation: 'landscape',
    pageMargins: [28, 28, 28, 34],
    content,
    // Page numbers matter on a detached compliance artefact: they are how a reader can
    // tell that the copy in front of them is missing pages.
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        { text: `${header.patientLabel} — ${tabLabel}`, color: DIM, fontSize: 7 },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: 'right',
          color: DIM,
          fontSize: 7,
        },
      ],
      margin: [28, 8, 28, 0],
    }),
    styles: {
      header: { fontSize: 16, bold: true },
      subheader: { fontSize: 12, bold: true },
    },
    defaultStyle: { fontSize: 9 },
  }
}

export function downloadAuditPdf(
  selection: AuditExportSelection,
  header: AuditPdfHeader,
  filename: string,
): void {
  pdfMake.createPdf(buildAuditPdfDefinition(selection, header)).download(filename)
}
